// Global Instructions Rule Applied!
import React, { useEffect, useLayoutEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { MobileTopBar } from './MobileTopBar.tsx'
import { MobileBottomNav } from './MobileBottomNav.tsx'
import { HomeScreen } from './screens/HomeScreen.tsx'
import { BrowseScreen } from './screens/BrowseScreen.tsx'
import { ListScreen } from './screens/ListScreen.tsx'
import { SearchScreen } from './screens/SearchScreen.tsx'
import { CompanyScreen } from './screens/CompanyScreen.tsx'
import { DealScreen } from './screens/DealScreen.tsx'
import { ContactScreen } from './screens/ContactScreen.tsx'
import { FundScreen } from './screens/FundScreen.tsx'
import { FundInvestorsScreen } from './screens/FundInvestorsScreen.tsx'
import { ProspectScreen } from './screens/ProspectScreen.tsx'
import { AccountScreen } from './screens/AccountScreen.tsx'
import { DesktopOnlyNotice } from './screens/DesktopOnlyNotice.tsx'
import { getDescriptor } from './lib/listDescriptors.ts'
import { store } from '../../../state_manager/store.ts'
import { saveRecents } from './state/recentsStorage.ts'

/**
 * Derive the top-bar title + whether to show a back button from the current path.
 * Detail screens and a module browse list are pushed onto the history stack, so they get a
 * back button; the Home / Browse / Search / Account tabs are roots.
 */
/** Browse-list route (`/browse/:module`) — owns its own scroll via useScrollRestore. */
const LIST_ROUTE = /^\/browse\/[^/]+/

function deriveTopBar(pathname: string): { title: string; showBack: boolean } {
  if (pathname.startsWith('/companies/')) return { title: 'Company', showBack: true }
  if (pathname.startsWith('/deals/')) return { title: 'Deal', showBack: true }
  if (pathname.startsWith('/contacts/')) return { title: 'Contact', showBack: true }
  if (pathname.startsWith('/funds/') && pathname.endsWith('/investors')) return { title: 'Investors', showBack: true }
  if (pathname.startsWith('/funds/')) return { title: 'Fund', showBack: true }
  if (pathname.startsWith('/fundraising/')) return { title: 'Prospect', showBack: true }
  if (pathname.startsWith('/browse/')) {
    const descriptor = getDescriptor(pathname.split('/')[2])
    return { title: descriptor ? descriptor.label : 'Browse', showBack: true }
  }
  if (pathname.startsWith('/browse')) return { title: 'Browse', showBack: false }
  if (pathname.startsWith('/account')) return { title: 'Account', showBack: false }
  if (pathname.startsWith('/search')) return { title: 'Search', showBack: false }
  if (pathname.startsWith('/home')) return { title: 'Reservoir', showBack: false }
  return { title: 'Reservoir', showBack: true }
}

/**
 * The mobile shell rendered below the 768px breakpoint instead of the desktop `Wrapper`.
 * Providers / Router / AuthGuard / Redux all sit above this in App.tsx, so the shell
 * inherits store + auth + router. It owns its own nested <Routes> that match the same
 * deep-link URLs the desktop uses (`/{module}/forms/full/:id`), plus a Home landing,
 * Browse hub + per-module lists, global search, an account tab, and a desktop-only
 * catch-all. "Stack navigation" is the browser history.
 */
export function MobileShell(): React.JSX.Element {
  const location = useLocation()
  const { title, showBack } = deriveTopBar(location.pathname)

  // Reset the shared <main> scroll to the top on navigation so a screen never inherits the
  // previous one's offset (e.g. landing on the short Browse hub from a scrolled detail left
  // it "scrolled up" out of view on iOS). Browse lists are exempt — ListScreen's
  // useScrollRestore owns their offset, and a reset here would clobber its cache-hit restore
  // (React runs the child's layout-effect before this parent one). Synchronous, pre-paint.
  useLayoutEffect(() => {
    if (LIST_ROUTE.test(location.pathname)) return
    const el = document.querySelector('main')
    if (el) el.scrollTop = 0
  }, [location.pathname])

  // Persist the mobile recents list to localStorage so it survives a cold PWA relaunch.
  // Kept here (not a store middleware) so persistence stays in the mobile layer; the
  // reference-equality guard limits writes to actual recents changes.
  useEffect(() => {
    let last = store.getState().mobileRecents?.items
    return store.subscribe(() => {
      const next = store.getState().mobileRecents?.items
      if (next !== last) {
        last = next
        saveRecents(next ?? [])
      }
    })
  }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: '#f5f5f5' }}>
      <MobileTopBar title={title} showBack={showBack} />
      <main style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Routes>
          <Route path="home" element={<HomeScreen />} />
          <Route path="browse" element={<BrowseScreen />} />
          <Route path="browse/:module" element={<ListScreen />} />
          <Route path="search" element={<SearchScreen />} />
          <Route path="companies/forms/full/:id" element={<CompanyScreen />} />
          <Route path="deals/forms/full/:id" element={<DealScreen />} />
          <Route path="contacts/forms/full/:id" element={<ContactScreen />} />
          <Route path="funds/forms/full/:id" element={<FundScreen />} />
          <Route path="funds/:id/investors" element={<FundInvestorsScreen />} />
          <Route path="fundraising/forms/full/:id" element={<ProspectScreen />} />
          <Route path="account" element={<AccountScreen />} />
          <Route path="*" element={<DesktopOnlyNotice />} />
        </Routes>
      </main>
      <MobileBottomNav />
    </div>
  )
}

export default MobileShell
